import type { ComponentProps, InputHTMLAttributes, ReactNode, SelectHTMLAttributes } from 'react'
import { ChevronDown } from './Icons'

export function Field({ label, error, hint, children, htmlFor }: { label: string; error?: string; hint?: ReactNode; children: ReactNode; htmlFor?: string }) {
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={htmlFor} className="text-[13px] font-medium tracking-[-0.005em] text-ink-800">
        {label}
      </label>
      {children}
      {error ? (
        <p role="alert" className="text-[13px] text-danger-600 animate-fade-in">
          {error}
        </p>
      ) : hint ? (
        <p className="text-[12.5px] text-ink-500">{hint}</p>
      ) : null}
    </div>
  )
}

export const controlClasses =
  'field-control h-10 rounded-[10px] bg-surface px-3.5 text-[14px] text-ink-900 shadow-[0_0_0_1px_oklch(0.165_0.012_76/0.1),inset_0_1px_2px_oklch(0.165_0.012_76/0.03)] transition-[box-shadow,background-color] duration-200 ease-spring hover:shadow-[0_0_0_1px_oklch(0.165_0.012_76/0.18),inset_0_1px_2px_oklch(0.165_0.012_76/0.03)] focus:outline-none focus:shadow-[0_0_0_1.5px_oklch(0.225_0.012_76),0_0_0_4px_oklch(0.225_0.012_76/0.12)] disabled:bg-ink-50 disabled:text-ink-400 aria-invalid:shadow-[0_0_0_1.5px_oklch(0.6_0.2_25)]'

export function Input({ className = '', mono = false, ...props }: InputHTMLAttributes<HTMLInputElement> & { mono?: boolean }) {
  return <input {...props} className={`${controlClasses} ${mono ? 'font-mono tabular text-[13px]' : ''} ${className}`} />
}

/** Input with a leading icon (search, etc.). */
export function IconInput({ icon, className = '', ...props }: InputHTMLAttributes<HTMLInputElement> & { icon: ReactNode }) {
  return (
    <div className={`relative ${className}`}>
      <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-ink-400">{icon}</span>
      <input {...props} className={`${controlClasses} pl-9.5`} />
    </div>
  )
}

export function Textarea({ className = '', ...props }: ComponentProps<'textarea'>) {
  return <textarea {...props} className={`${controlClasses} h-auto resize-none py-2.5 ${className}`} />
}

export function Select({ className = '', ...props }: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <div className={`relative ${className}`}>
      <select {...props} className={`${controlClasses} appearance-none pr-9`} />
      <ChevronDown size={14} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-ink-400" />
    </div>
  )
}

/** Custom-drawn checkbox with the ink palette. */
export function Checkbox({ label, className = '', ...props }: InputHTMLAttributes<HTMLInputElement> & { label: ReactNode }) {
  return (
    <label className={`group inline-flex cursor-pointer select-none items-center gap-2.5 text-[13.5px] text-ink-700 ${className}`}>
      <span className="relative inline-flex size-[18px] items-center justify-center">
        <input type="checkbox" {...props} className="peer absolute inset-0 size-full cursor-pointer appearance-none rounded-[5px] bg-surface shadow-[0_0_0_1px_oklch(0.165_0.012_76/0.18),inset_0_1px_2px_oklch(0.165_0.012_76/0.04)] transition-[background-color,box-shadow] duration-200 ease-spring checked:bg-ink-900 checked:shadow-none group-hover:shadow-[0_0_0_1px_oklch(0.165_0.012_76/0.3)] group-hover:checked:shadow-none" />
        <svg viewBox="0 0 16 16" className="pointer-events-none relative size-3 scale-50 text-paper opacity-0 transition-[opacity,transform] duration-200 ease-spring peer-checked:scale-100 peer-checked:opacity-100" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="m3.5 8.5 3 3 6-7" />
        </svg>
      </span>
      {label}
    </label>
  )
}

/** Segmented control: a row of mutually exclusive options. */
export function Segmented<T extends string>({ value, options, onChange, className = '', 'aria-label': ariaLabel }: { value: T | null; options: { value: T; label: ReactNode }[]; onChange: (v: T) => void; className?: string; 'aria-label'?: string }) {
  return (
    <div role="radiogroup" aria-label={ariaLabel} className={`inline-flex h-10 items-center gap-0.5 rounded-[11px] bg-ink-950/[0.05] p-[3px] ${className}`}>
      {options.map((o) => {
        const active = o.value === value
        return (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => onChange(o.value)}
            className={`h-full rounded-[8px] px-3 text-[13px] font-medium transition-[background-color,color,box-shadow,transform] duration-200 ease-spring active:scale-[0.98] ${
              active ? 'bg-surface text-ink-900 shadow-[0_1px_2px_oklch(0.165_0.012_76/0.08),0_0_0_1px_oklch(0.165_0.012_76/0.05)]' : 'text-ink-600 hover:text-ink-900'
            }`}
          >
            {o.label}
          </button>
        )
      })}
    </div>
  )
}
